import React, { useEffect } from 'react'
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { Button, Box, Text, Flex, Spinner } from '@chakra-ui/react'
import { Title } from '@zumma/ui'
import { router } from '../components/routing/Router'
import * as Sentry from '@sentry/react'
import { isChunkLoadError, attemptChunkErrorRecovery } from '../utils/chunkLoadError'
import { useTranslation } from 'react-i18next'

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation()
  useEffect(() => {
    if (isChunkLoadError(error)) {
      attemptChunkErrorRecovery()
    }
  }, [error])

  if (isChunkLoadError(error)) {
    return (
      <Flex
        justifyContent='center'
        alignItems='center'
        minH='100vh'
      >
        <Spinner size='xl' />
      </Flex>
    )
  }

  return (
    <Box
      p={8}
      textAlign='center'
    >
      <Title>{t('shared.errorBoundary.title')}</Title>
      <Text mb={4}>{t('shared.errorBoundary.unexpectedError')}</Text>
      <Button onClick={resetErrorBoundary}>{t('shared.errorBoundary.goHome')}</Button>
    </Box>
  )
}

export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ReactErrorBoundary
      FallbackComponent={Fallback}
      onReset={() => router.navigate('/')}
      onError={(error, errorInfo) => {
        if (isChunkLoadError(error)) return

        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack
            }
          }
        })
      }}
    >
      {children}
    </ReactErrorBoundary>
  )
}
